// @flow
/* eslint-disable react/jsx-no-bind */
import * as React from 'react';

import PjpBarChart from 'common/entity/pjpMaster/pjpInsights/charts/PjpBarChart';
import PjpPieChart from 'common/entity/pjpMaster/pjpInsights/charts/PjpPieChart';
import PjpMetricChartCard from './PjpMetricChartCard';
import type { PjpMetricGraphModelType } from 'common/models/flow/PjpMetricGraph';
import { ChartTypeEnum } from 'common/enums/ChartEnums';
import { themr, type ThemeType } from 'react-css-themr';
import Checkbox from 'common/components/core/checkbox';
import { Loader } from 'common/components/core/loader/Loader';

import {
    Box,
    Columns,
    Column,
    Stack,
    Text,
    Menu,
    MenuItem,
    MenuButton,
    MenuList,
    Tooltip
} from '@locus-taxy/neo-ui';
import defaultTheme from './PjpMetricGridCard.scss';

import {
    type PjpMetricCardProps,
    type ChartType
} from 'common/entity/pjpMaster/pjpDataTypes/insights/PjpMetricCardDataTypes';

import type { PjpInsightsDashboard } from 'common/models/internal/PjpInsightsDashboard';

import { type TFunction } from 'react-i18next';
import {
    PermissionMatch,
    AccessDeniedMessage,
    RoleOperationEnum
} from 'common/modules/rbac';

type Props = {
    graph: PjpMetricGraphModelType,
    dashboard: PjpInsightsDashboard,
    metricCard: PjpMetricCardProps,
    alignLegend?: 'right',
    gridWidth: number,
    minCardHeight: number,
    disableFooter: boolean,
    cardHeight: number,
    currentCols: number,
    dialogActive: boolean,
    theme: ThemeType,
    className: string,
    graphsWithExistingBeat: Map<string, boolean>,
    disableExport: boolean,
    t: TFunction,
    onDeleteChart: (dashboardId: string) => void,
    onEditChart: (dashboardId: string) => void,
    toggleExistingBeat: (graphId: string) => void,
    currentPlanData: ChartType,
    existingPlanData: ChartType,
    customHeight?: number,
    clientId: string,
    existingBeatEnabled: boolean,
    isMetricLoading: boolean,
    metricHasData: boolean
};

type PjpMetricCardHeaderProps = {
    graph: PjpMetricGraphModelType,
    theme: ThemeType,
    metricCard: PjpMetricCardProps,
    t: TFunction
};

class PjpMetricCardHeader extends React.PureComponent<PjpMetricCardHeaderProps> {
    render() {
        const { graph, metricCard, t } = this.props;
        const aggregateExistingValue = metricCard.aggregateExistingValue
            ? metricCard.aggregateExistingValue
            : null;
        const existingAggValue = aggregateExistingValue
            ? aggregateExistingValue.currentValue
            : '';
        const { currentValue } = metricCard.aggregate;
        const queries = graph.queries.get(0);
        const { metric, aggregationType, groupBy } = queries;
        const groupByLabel = groupBy.map(group => {
            return t(
                `entityManagement:pjpAnalytics.groupBys.${group.toLowerCase()}`
            );
        });
        return (
            <Box padding="20px">
                <Stack>
                    <Columns space="xxSmall">
                        <Column width="content">
                            <Text fontSize={3}>
                                {t(
                                    `entityManagement:pjpAnalytics.aggregationType.${aggregationType}`
                                )}{' '}
                                of{' '}
                            </Text>
                        </Column>
                        <Column width="fluid">
                            <Text fontSize={3} fontWeight="600">
                                {t(`entityManagement:pjpAnalytics.${metric}`)}
                            </Text>
                        </Column>
                    </Columns>
                    <Text color="grey.600" fontSize={2} fontWeight="400">
                        by {groupByLabel.join(',')}
                    </Text>
                    {aggregationType === 'AVG' ? (
                        <Text fontWeight="600" fontSize={1}>
                            {currentValue}
                            {aggregateExistingValue
                                ? ` & ${existingAggValue}`
                                : null}
                        </Text>
                    ) : null}
                </Stack>
            </Box>
        );
    }
}

type PjpGritItemFooterProps = {
    toggleExistingBeat: (graphId: string) => void,
    onDeleteChart: (graphId: string) => void,
    onEditChart: (graphId: string) => void,
    graph: PjpMetricGraphModelType,
    existingBeatEnabled: boolean,
    theme: ThemeType,
    t: TFunction
};

class PjpGridItemFooter extends React.PureComponent<PjpGritItemFooterProps> {
    includeExistingBeatToggle = () => {
        const { toggleExistingBeat, graph } = this.props;
        toggleExistingBeat && toggleExistingBeat(graph.id);
    };

    onDeleteClick = () => {
        const { onDeleteChart, graph } = this.props;
        onDeleteChart(graph.id);
    };

    editChart = () => {
        const { onEditChart, graph } = this.props;
        onEditChart(graph.id);
    };

    render() {
        const { theme, existingBeatEnabled, t } = this.props;
        return (
            <Box px={6} py={3} bg="grey.100" mt="auto">
                <PermissionMatch
                    operations={RoleOperationEnum.EDIT_PJP}
                    render={hasPermission => {
                        return (
                            <Columns alignY="center">
                                <Column width="fluid">
                                    <Tooltip
                                        shouldWrapChildren
                                        tooltip={
                                            !hasPermission ? (
                                                <AccessDeniedMessage
                                                    action={t(
                                                        `translations:edit`
                                                    )}
                                                />
                                            ) : null
                                        }
                                    >
                                        <Checkbox
                                            smallCheckbox
                                            label={t(
                                                'entityManagement:pjpAnalytics.includeExistingBeat'
                                            )}
                                            theme={theme}
                                            checked={existingBeatEnabled}
                                            onChange={
                                                this.includeExistingBeatToggle
                                            }
                                            disabled={!hasPermission}
                                        />
                                    </Tooltip>
                                </Column>
                                <Column width="content">
                                    <Menu>
                                        <Tooltip
                                            shouldWrapChildren
                                            tooltip={
                                                !hasPermission ? (
                                                    <AccessDeniedMessage
                                                        action={t(
                                                            `translations:edit`
                                                        )}
                                                    />
                                                ) : null
                                            }
                                        >
                                            <MenuButton
                                                appearance="icon"
                                                icon="overflow_horizontal"
                                                size="large"
                                                disabled={!hasPermission}
                                            />
                                        </Tooltip>
                                        <MenuList>
                                            <MenuItem
                                                onClick={this.onDeleteClick}
                                            >
                                                {t(
                                                    'entityManagement:pjpAnalytics.delete'
                                                )}
                                            </MenuItem>
                                            <MenuItem onClick={this.editChart}>
                                                {t(
                                                    'entityManagement:pjpAnalytics.edit'
                                                )}
                                            </MenuItem>
                                        </MenuList>
                                    </Menu>
                                </Column>
                            </Columns>
                        );
                    }}
                />
            </Box>
        );
    }
}

const LegendProps = {
    hideLegendValue: false,
    align: 'center',
    layout: 'horizontal',
    verticalAlign: 'bottom',
    wrapperStyle: {
        width: 'width'
    }
};

export const PjpMetricCard = (props: Props) => {
    const renderFooter = () => {
        return props.disableFooter ? null : <PjpGridItemFooter {...props} />;
    };

    const renderHeader = () => {
        return <PjpMetricCardHeader {...props} />;
    };

    const renderChart = params => {
        const { chart } = props.metricCard;
        const { currentPlanData, existingPlanData, customHeight } = props;
        if (!currentPlanData && !existingPlanData) {
            return 'No data founds';
        }

        switch (chart.type) {
            case ChartTypeEnum.Pie:
                return (
                    <Box>
                        <PjpPieChart
                            customHeight={customHeight || 0}
                            existingPlan={existingPlanData}
                            currentPlan={currentPlanData}
                            key={props.graph.id}
                            legendProps={LegendProps}
                            width={params.width}
                            height={params.height}
                            data={chart.data}
                            theme={props.theme}
                            unit={chart.metricUnit}
                        />
                    </Box>
                );

            case ChartTypeEnum.Bar:
                return (
                    <Stack>
                        <PjpBarChart
                            height={params.height}
                            width={params.width}
                            relativeWidth={params.relativeWidth}
                            relativeHeight={params.relativeHeight}
                            alignLegend={props.alignLegend}
                            data={currentPlanData || existingPlanData}
                            unit={chart.metricUnit}
                            selectedDataKeys={[]}
                            theme={props.theme}
                        />
                    </Stack>
                );
            default:
                return null;
        }
    };
    if (props.isMetricLoading && !props.metricHasData) {
        return (
            <Column>
                <Loader active />
            </Column>
        );
    }
    return props.metricCard ? (
        <>
            <PjpMetricChartCard
                isLoading={props.isMetricLoading}
                emptyState={
                    !props.currentPlanData && !props.existingPlanData
                        ? true
                        : false
                }
                cardHeader={renderHeader}
                graph={props.graph}
                metricCard={props.metricCard}
                cardHeight={props.cardHeight}
                minCardHeight={props.minCardHeight}
                gridWidth={props.gridWidth}
                currentCols={props.currentCols}
                renderFooter={renderFooter}
                renderChart={renderChart}
                hideAggregateInHeader={
                    props.metricCard.chart.type === ChartTypeEnum.BigNumber
                }
                autoReloadEnabled={false}
                dialogActive={false}
                theme={props.theme}
                className={props.className}
            />
        </>
    ) : null;
};
const ThemedPjpMetricCard = themr('PjpMetricCard', defaultTheme)(PjpMetricCard);
export default ThemedPjpMetricCard;
